// The year, as a ruler on the bottom edge of the screen: twelve month cells of
// three ten-day sections, and a marker.
//
// The sections were briefly drawn at a height proportional to the rating there,
// which is what the design handoff asked for. It was dropped after use. Two
// things went wrong with it: 36 bars at 36 heights read as a CHART, so the eye
// tries to interpret the shape rather than use the control; and a reader aiming
// at a section had to work out which stripe was a target, because the 3px ones
// did not look like one. Uniform sections are a scale you can hit. The question
// the heights were answering — when is this place good — belongs to the
// briefing's calendar, which has the room to answer it.
//
// Like the track before it, this module never decides a date. It reads state and
// writes DOM. Moving to a dekad is showFrame's job, and everything downstream of
// that is a `dateIdx` subscriber — so nothing here calls out, it is called.
//
// The arithmetic lives in lib/ruler-geometry.ts, away from the DOM, where it can
// be tested. What is left here is genuinely only markup.

import { $ } from '../dom.ts';
import { state } from '../state.ts';
import { NO_HOVER, REDUCED_MOTION } from './breakpoints.ts';
import { MONTH_ABBR } from '../../lib/city-stats.ts';
import { sectionLabel } from '../../lib/sections.ts';
import { markerLeftPct, sweepLeftPct, idxFromFraction } from '../../lib/ruler-geometry.ts';

const DEKADS_PER_MONTH = 3;

// Build the twelve month cells once. Every section is the same size — CSS gives
// them their height — so this lays out the boxes and nothing re-measures them;
// only their past/current colouring changes, and that is updateRulerMarker's.
export function buildRuler(): void {
  const cells = $('ruler-cells');
  cells.replaceChildren();
  const n = state.dates.length;
  if (!n) return;
  const months = Math.ceil(n / DEKADS_PER_MONTH);
  for (let m = 0; m < months; m++) {
    const cell = document.createElement('div');
    cell.className = 'ru-month';

    const ticks = document.createElement('div');
    ticks.className = 'ru-ticks';
    for (let p = 0; p < DEKADS_PER_MONTH; p++) {
      const i = m * DEKADS_PER_MONTH + p;
      if (i >= n) break;
      const tick = document.createElement('div');
      tick.className = 'ru-tick';
      tick.dataset.i = String(i);
      ticks.appendChild(tick);
    }

    const line = document.createElement('div');
    line.className = 'ru-line';

    const label = document.createElement('div');
    label.className = 'ru-label';
    label.dataset.m = String(m);
    label.textContent = MONTH_ABBR[m % 12];

    cell.append(ticks, line, label);
    cells.appendChild(cell);
  }
  updateRulerMarker();
}

// Where the marker is PART WAY through the showing frame, during playback.
//
// The data steps — one frame per ten days — but time does not, and the marker is
// a clock rather than a readout of which frame is loaded. A quartz watch ticks
// once a second; a good mechanical one sweeps, because the hand is showing a
// continuous quantity. So over the seconds a frame is up, the marker crosses
// that frame's ten days and lands on the next dekad's centre exactly as the
// frame flips: sweepLeftPct(i, 1) === markerLeftPct(i + 1), which is what makes
// the handover invisible.
//
// Only the marker moves this way. The ticks, their past/current classes and the
// month label stay discrete, because those ARE the data and the data is stepped.
// `left` stays the dekad the marker BELONGS to and the sweep rides on a
// transform, because this runs on every animation frame: moving an element by
// `left` is a layout every frame, and a transform is a composite. The offset is
// still derived from sweepLeftPct, so there is one account of where the marker
// should be and the last-dekad clamp comes with it.
let rulerWidth = 0; // measured lazily; a read per frame would be its own reflow
export function updateRulerSweep(progress: number): void {
  const n = state.dates.length;
  const marker = $('ruler-marker');
  if (!n || REDUCED_MOTION.matches) {
    marker.style.transform = '';
    return;
  }
  if (!rulerWidth) rulerWidth = $('ruler').getBoundingClientRect().width;
  const rest = markerLeftPct(state.dateIdx, n);
  const swept = sweepLeftPct(state.dateIdx, progress, n);
  marker.style.transform = `translateX(${(((swept - rest) / 100) * rulerWidth).toFixed(2)}px)`;
}

// The cached width is only wrong after a resize, and only until the next frame.
export function invalidateRulerWidth(): void {
  rulerWidth = 0;
}

// Where we are in the year: the marker's position, the ticks' past/current
// classes, the current month's label, and the invisible range input's value —
// all four are the same fact, so they are written together. This is also what
// puts the marker back on a dekad's centre when playback stops mid-sweep.
export function updateRulerMarker(): void {
  const n = state.dates.length;
  const cur = state.dateIdx;

  const marker = $('ruler-marker');
  marker.style.left = `${markerLeftPct(cur, n)}%`;
  marker.style.transform = ''; // drop any sweep left over from a paused step
  marker.hidden = n === 0;

  for (const tick of document.querySelectorAll<HTMLElement>('#ruler-cells .ru-tick')) {
    const i = Number(tick.dataset.i);
    tick.classList.toggle('on', i < cur);
    tick.classList.toggle('cur', i === cur);
  }
  const curMonth = Math.floor(cur / DEKADS_PER_MONTH);
  for (const label of document.querySelectorAll<HTMLElement>('#ruler-cells .ru-label')) {
    label.classList.toggle('cur', Number(label.dataset.m) === curMonth);
  }

  const range = $<HTMLInputElement>('ruler-range');
  if (range.value !== String(cur)) range.value = String(cur);
  range.setAttribute('aria-valuetext', n ? sectionLabel(state.dates[cur]) : '');
}

// The scrub, the keyboard and the hover preview.
//
// An invisible native range input still sits over the ticks, exactly as it did
// over the squares. It is not laziness: drag, ←/→, Home/End, the focus ring and
// a real accessible name all come free, and a pointer-events reimplementation
// would owe every one of them. The one thing it costs is that the input swallows
// pointer events destined for the ticks — so the hover preview is driven from
// the input's own mousemove rather than from the ticks' :hover.
export function wireRuler({ pause, showFrame, persist }: {
  pause: () => void;
  showFrame: (idx: number) => void;
  persist: () => void;
}): void {
  const range = $<HTMLInputElement>('ruler-range');

  range.addEventListener('input', (e) => {
    pause();
    showFrame(Number((e.target as HTMLInputElement).value));
    persist();
  });

  // Hover is a device capability, not a width — a small desktop window still
  // hovers and a large tablet still does not.
  if (NO_HOVER.matches) return;
  const cells = $('ruler-cells');
  let hovered: HTMLElement | null = null;
  const setHover = (el: HTMLElement | null) => {
    if (hovered === el) return;
    hovered?.classList.remove('hov');
    el?.classList.add('hov');
    hovered = el;
  };
  range.addEventListener('mousemove', (e) => {
    const n = state.dates.length;
    if (!n) return;
    const r = range.getBoundingClientRect();
    if (!r.width) return;
    const idx = idxFromFraction((e.clientX - r.left) / r.width, n);
    setHover(cells.querySelector<HTMLElement>(`.ru-tick[data-i="${idx}"]`));
  });
  range.addEventListener('mouseleave', () => setHover(null));
}
